import { ServiceRequestPerformerIdentifier } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import type { PaginationQueryParams } from '../domain/pagination';
import {
  CodingSchema,
  DateTimeSchema,
  type PaginatedBundle,
  PaginatedBundleSchema,
} from '../domain/schema';
import {
  ObservationPathologySchema,
  ObservationResultSchema,
} from '../observation/schema';
import type { WithId } from '../types';

export const pathologyOrderStatus = [
  'draft',
  'active',
  'on-hold',
  'revoked',
  'completed',
  'entered-in-error',
  'unknown',
] as const;

/**
 * Based on what Ampath requires (Ampath.CreateOrderRequest)
 */
export const PathologyOrderSchema = z.object({
  patient: DisplayReferenceSchema,
  requestor: DisplayReferenceSchema,
  testCodes: z.array(CodingSchema).min(1),
  orderNumber: z.string().optional(),
  orderId: z.string().optional(),
  status: z.enum(pathologyOrderStatus),
  /**
   * The date/time that the physical sample was taken from the patient
   */
  sampleDateTime: z.string().datetime(),
  copyDoctors: z.array(DisplayReferenceSchema).optional(),
  comment: z.string().optional(),
});

export type PathologyOrder = z.infer<typeof PathologyOrderSchema>;

export const PathologyOrderGetResponseSchema = PathologyOrderSchema.partial();

export type PathologyOrderGetResponse = z.infer<
  typeof PathologyOrderGetResponseSchema
>;

export const PathologyOrderGetResponseWithIdSchema =
  PathologyOrderGetResponseSchema.partial().extend({
    id: UUIDSchema,
  });
export type PathologyOrderGetResponseWithId = z.infer<
  typeof PathologyOrderGetResponseWithIdSchema
>;

export const PathologyOrderGetAllResponseSchema = PaginatedBundleSchema.extend({
  orders: PathologyOrderGetResponseWithIdSchema.array(),
});

export interface PathologyOrderGetAllResponse extends PaginatedBundle {
  orders: WithId<PathologyOrderGetResponse>[];
}

export const PathologyOrderCreateRequestBaseSchema = z.object({
  testCodes: z.array(z.string()).min(1),
  sampleDateTime: z.string(),
  copyDoctors: z.array(z.string()).optional(),
  comment: z.string().optional(),
});
export type PathologyOrderCreateRequestBase = z.infer<
  typeof PathologyOrderCreateRequestBaseSchema
>;

export const PathologyOrderCreateRequestSchema =
  PathologyOrderCreateRequestBaseSchema.extend({
    patient: UUIDSchema,
  });
export type PathologyOrderCreateRequest = z.infer<
  typeof PathologyOrderCreateRequestSchema
>;

export const PathologyPatientOrderCreateSchema =
  PathologyOrderCreateRequestSchema.extend({
    patient: UUIDSchema,
  });
export type PathologyPatientOrderCreateRequest = z.infer<
  typeof PathologyPatientOrderCreateSchema
>;

export const PathologyOrderGetAllParamsSchema = z.object({
  subject: UUIDSchema.optional(),
});
export type PathologyOrderGetAllParams = z.infer<
  typeof PathologyOrderGetAllParamsSchema
> &
  PaginationQueryParams;

//Pathology reports

export const pathologyReportStatus = [
  'registered',
  'partial',
  'preliminary',
  'final',
  'amended',
  'corrected',
  'appended',
  'cancelled',
  'entered-in-error',
  'unknown',
] as const;

export const pathologyReportContentTypes = ['application/pdf'] as const;

export const PathologyPerformerCodeSchema = z.nativeEnum(
  ServiceRequestPerformerIdentifier,
);

export const PathologyReportItemSchema = z.object({
  contentType: z.enum(pathologyReportContentTypes),
  hash: z.string(),
  title: z.string(),
  url: z.string().url(),
});

export type PathologyReportItem = z.infer<typeof PathologyReportItemSchema>;

export const PathologyReportSchema = z.object({
  patient: DisplayReferenceSchema,
  orderNumber: z.string().optional(),
  status: z.enum(pathologyReportStatus),
  performer: PathologyPerformerCodeSchema,
  issued: z.string().datetime(),
  order: DisplayReferenceSchema,
  reports: PathologyReportItemSchema.array().min(1),
  results: ObservationPathologySchema.optional(),
});

export type PathologyReport = z.infer<typeof PathologyReportSchema>;

export const PathologyReportGetAllParamsSchema = z.object({
  subject: UUIDSchema.optional(),
  _include: z.string().optional(),
});

export const PathologyReportGetResponseSchema =
  PathologyReportSchema.partial().extend({
    id: UUIDSchema,
  });
export type PathologyReportGetResponse = z.infer<
  typeof PathologyReportGetResponseSchema
>;

export const PathologyReportGetAllResponseSchema = PaginatedBundleSchema.extend(
  {
    reports: PathologyReportGetResponseSchema.extend({
      id: UUIDSchema,
    }).array(),
  },
);
export interface PathologyReportGetAllResponse extends PaginatedBundle {
  reports: WithId<PathologyReportGetResponse>[];
}

export type PathologyReportGetAllParams = z.infer<
  typeof PathologyReportGetAllParamsSchema
> &
  PaginationQueryParams;

// New Models for QM pathology

export enum PathologyProvider {
  lancet = 'pathology.provider.lancet',
}

export const PathologyProviderSchema = z.nativeEnum(PathologyProvider);

export const PathologyCreateOrderRequestSchema = z.object({
  doctors: z
    .string()
    .array()
    .describe('IDs of doctors/practices you want to cc results to')
    .optional(),
  comment: z
    .string()
    .optional()
    .describe('Allow practitioner/user to add comment'),
  tests: z
    .string()
    .array()
    .min(1)
    .describe('The tests that will be submitted to the pathology provider'),
  collectionDateTime: DateTimeSchema,
  patient: z.string().describe('Patient ID'),
  provider: PathologyProviderSchema,
  practitioner: z.string().describe('Practitioner ID'),
});

export const PathologyGetTestsRequestSchema = z.object({
  limit: z.number().default(10),
  query: z.string().optional(),
  provider: PathologyProviderSchema,
});

export const PathologyGetTestsResponseSchema = z
  .object({
    description: z.string(),
    code: z.string(),
  })
  .array();

export const PathologyReportWithObservationsResponseSchema = z.object({
  pathology: PathologyReportGetResponseSchema.extend({
    id: UUIDSchema,
  }),
  observations: ObservationResultSchema.array().optional(),
});

export const PathologyReportGetAllObservationsResponseSchema =
  PaginatedBundleSchema.extend({
    results: PathologyReportWithObservationsResponseSchema.array(),
  });

export type PathologyReportGetAllObservationsResponse = z.infer<
  typeof PathologyReportGetAllObservationsResponseSchema
>;

export const PathologyCreateOrderWebRequestSchema =
  PathologyCreateOrderRequestSchema.omit({
    practitioner: true,
  });

export const PathologyCreateOrderFormRequestSchema =
  PathologyCreateOrderRequestSchema.omit({
    practitioner: true,
    provider: true,
    patient: true,
  });

export type PathologyCreateOrderRequest = z.infer<
  typeof PathologyCreateOrderRequestSchema
>;
export type PathologyCreateOrderWebRequest = z.infer<
  typeof PathologyCreateOrderWebRequestSchema
>;
export type PathologyCreateOrderFormRequest = z.infer<
  typeof PathologyCreateOrderWebRequestSchema
>;

export type PathologyGetTestsRequest = z.infer<
  typeof PathologyGetTestsRequestSchema
>;
export type PathologyGetTestsResponse = z.infer<
  typeof PathologyGetTestsResponseSchema
>;
export type PathologyReportWithObservationsResponse = z.infer<
  typeof PathologyReportWithObservationsResponseSchema
>;
